import React from 'react'
import { Typography, IconButton } from '@mui/material'
import { Box } from '@mui/system'
import { FIELD_TYPES, FORMATS } from '../../utility/constants'
import LockingTooltip from '../LockingTooltip'
import LongMenu from '../../pages/DataEntryPage/Fields/LongMenu'
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { formatCurrency } from '../../pages/DataEntryPage/utils/currency'
import moment from 'moment'

function MyTasksCardField({
  item,
  index,
  openMenuIndex,
  setOpenMenuIndex,
  setFilePreview,
  downloadFile,
}) {
  const { key, value, dataStructure } = item

  let displayContent
  let tooltipContent

  const isTimestampedBigText =
    ([
      FIELD_TYPES.TIMESTAMPED_BIGTEXT,
      FIELD_TYPES.TIMESTAMPED_DATE,
      FIELD_TYPES.TIMESTAMPED_DATETIME,
    ].includes(dataStructure) &&
      Array.isArray(value)) ||
    (dataStructure === FIELD_TYPES.LIST &&
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === 'object' &&
      'text' in value[0] &&
      'timestamp' in value[0])

  if (isTimestampedBigText) {
    displayContent =
      value.length > 0 ? (
        <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column' }}>
          {value.slice(0, 3).map((entry, i) => (
            <span key={i}>
              <strong>{entry.timestamp}:</strong> {entry.text}
            </span>
          ))}
          {value.length > 3 && <span>...</span>}
        </Box>
      ) : (
        '--'
      )

    tooltipContent = (
      <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column' }}>
        {value.map((entry, i) => (
          <span key={i}>
            <strong>{entry.timestamp}:</strong> {entry.text}
          </span>
        ))}
      </Box>
    )
  } else if (dataStructure === FIELD_TYPES.LIST_MULTI) {
    const visibleValues = value.slice(0, 3)

    displayContent =
      value.length > 0 ? (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            flexDirection: 'column',
            gap: 1, // 8px spacing between lines
          }}
        >
          {visibleValues.map((val, i) => (
            <strong key={i}>{val}</strong>
          ))}
          {value.length > 3 && <span>...</span>}
        </Box>
      ) : (
        '--'
      )

    tooltipContent = (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          flexDirection: 'column',
          gap: 1, // 8px spacing between lines
        }}
      >
        {value.map((val, i) => (
          <span key={i}>{val}</span>
        ))}
      </Box>
    )
  } else if (dataStructure === FIELD_TYPES.LIST && Array.isArray(value)) {
    const visibleValues = value.slice(0, 3)

    displayContent =
      value.length > 0 ? (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {visibleValues.map((val, i) => (
            <strong key={i}>
              {typeof val === 'object' ? val.label || val.value || JSON.stringify(val) : val}
            </strong>
          ))}
          {value.length > 3 && <span>...</span>}
        </Box>
      ) : (
        '--'
      )

    tooltipContent = (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {value.map((val, i) => (
          <span key={i}>
            {typeof val === 'object' ? val.label || val.value || JSON.stringify(val) : val}
          </span>
        ))}
      </Box>
    )
  } else if (dataStructure === FIELD_TYPES.DATE && value) {
    const formatted = moment(value).isValid() ? moment(value).format(FORMATS.DATE) : value
    displayContent = <strong>{formatted}</strong>
    tooltipContent = formatted
  } else if (dataStructure === FIELD_TYPES.DATETIME && value) {
    const formatted = moment(value).isValid() ? moment(value).format(FORMATS.DATETIME) : value
    displayContent = <strong>{formatted}</strong>
    tooltipContent = formatted
  } else if (dataStructure === FIELD_TYPES.CURRENCY && value) {
    const formatted = formatCurrency(value)
    displayContent = <strong>{formatted}</strong>
    tooltipContent = formatted
  } else if (dataStructure === FIELD_TYPES.FILE && value) {
    displayContent = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'nowrap',
          minWidth: 0,
        }}
      >
        <Typography
          component="span"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 'calc(100% - 30px)',
            flexGrow: 1,
          }}
        >
          <strong>{value}</strong>
        </Typography>
      </Box>
    )

    tooltipContent = value
  } else {
    let safeValue

    if (
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '') ||
      (typeof value === 'object' && value !== null && Object.keys(value).length === 0)
    ) {
      safeValue = '--'
    } else if (typeof value === 'object') {
      safeValue = value.value || '--'
    } else {
      safeValue = value
    }

    displayContent = <strong>{safeValue}</strong>
    tooltipContent = safeValue
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <LockingTooltip
        title={tooltipContent}
        arrow
        open={openMenuIndex === index ? false : undefined}
        componentsProps={{
          tooltip: {
            sx: {
              fontSize: '9px',
              fontWeight: '500',
            },
          },
        }}
      >
        <Typography component="span" className="task-subtext text-ellipsis">
          <span>{key}:</span> {displayContent}
        </Typography>
      </LockingTooltip>

      {dataStructure === FIELD_TYPES.FILE && (
        <LongMenu
          options={[
            {
              label: 'Preview',
              icon: (
                <IconButton size="small" sx={{ padding: '0px 4px' }}>
                  <RemoveRedEyeOutlinedIcon fontSize="18px" />
                </IconButton>
              ),
              onClick: () => {
                setOpenMenuIndex(null)
                setFilePreview(value)
              },
            },
            {
              label: 'Download',
              icon: (
                <IconButton size="small" sx={{ padding: '0px 4px' }}>
                  <FileDownloadOutlinedIcon fontSize="18px" />
                </IconButton>
              ),
              onClick: () => {
                setOpenMenuIndex(null)
                downloadFile(value)
              },
            },
          ]}
          onOpen={() => setOpenMenuIndex(index)}
          onClose={() => setOpenMenuIndex(null)}
        />
      )}
    </div>
  )
}

export default React.memo(MyTasksCardField)
